import React, { useState, useEffect } from "react";
import { canManageRewards as canManageRewardsFor } from "../lib/rewards-access";
import { canBeProposedAsParticipant } from "../lib/participant-eligibility";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Trophy, Target, TrendingUp, Users, ArrowLeft, Play, Loader2, Flame, ChevronRight, Zap, LogOut, Calendar, Clock, Crosshair, BookOpen, Building2, Package, UserCircle, Star, BarChart3, History as HistoryIcon, Plus, CheckCircle2 } from "lucide-react";
import type { User, AIAgent, ActiveChallengeInfo, LeaderboardEntry, LeaderboardResponse, ChallengeTeam, PageType } from "../types";
import { challengesApi, bizCoinsApi, type DuelData } from "../lib/api";
import { ScopeRewardsModeControl } from "../components/ScopeRewardsModeControl";
import { DuelsView } from "../components/DuelsView";
import { TournamentsView } from "../components/TournamentsView";
import { Swords } from "lucide-react";
import { useTranslation } from "react-i18next";
import { pickScoringLabel } from "../lib/scoring-i18n";
import { ChallengeBriefPage } from "../components/ChallengeBriefPage";
import { SessionsWorkspace, StatCard, type WorkspaceSection } from "../components/SessionsWorkspace";
import { HistoryPage } from "./HistoryPage";
import {
  pickChallengeTitle,
  pickChallengeDescription,
  pickChallengeFictiveCompanyName,
  pickChallengeFictiveCompanyDescription,
  pickChallengeGiftName,
} from "../lib/challenge-i18n";

interface ChallengePageProps {
  user: User;
  challengeId?: string;
  onStartTraining?: (agent: AIAgent, challengeId: string) => void;
  onBack?: () => void;
  onSelectChallenge?: (id: string) => void;
  // Étape 8 — lancer une passe de duel (résout le simulateur + lance l'entraînement).
  onPlayDuel?: (duel: DuelData) => void;
  /** Lien « Historique de l'équipe » (Pilot) de l'onglet Historique — même câblage que la page Historique. */
  onOpenTeamHistory?: () => void;
}

export function ChallengePage({ user, challengeId, onStartTraining, onBack, onSelectChallenge, onPlayDuel, onOpenTeamHistory }: ChallengePageProps) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("all");
  // Étape 9B — override du mode de récompense pour ce challenge (organisateurs).
  // Liste partagée avec le serveur (`src/api/biz-coins.ts`) ; CSL retiré (décision du 30/09/2026).
  const canManageRewards = canManageRewardsFor(user.profileType);
  const [tenantMode, setTenantMode] = useState<"virtual" | "catalog">("virtual");
  useEffect(() => {
    if (canManageRewards) bizCoinsApi.getWallet().then((w) => setTenantMode(w.mode)).catch(() => {});
  }, [canManageRewards]);

  useEffect(() => {
    if (!challengeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    challengesApi.getLeaderboard(challengeId)
      .then((res) => {
        setData(res as LeaderboardResponse);
      })
      .catch((err) => {
        setError(err.message || t("challenge.errorLoading"));
      })
      .finally(() => setLoading(false));
  }, [challengeId]);

  // List of available challenges
  const [myChallenges, setMyChallenges] = useState<ActiveChallengeInfo[]>([]);
  const [loadingList, setLoadingList] = useState(!challengeId);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const [joinConfirmChallenge, setJoinConfirmChallenge] = useState<ActiveChallengeInfo | null>(null);
  const [joinTeams, setJoinTeams] = useState<Array<{ id: string; name: string; color?: string; members: Array<{ firstName: string; lastName: string }> }>>([]);
  const [joinSelectedTeamId, setJoinSelectedTeamId] = useState<string | null>(null);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [challengeFilter, setChallengeFilter] = useState<"all" | "intra" | "inter">("all");
  const [accountFilter, setAccountFilter] = useState<string>(""); // "" = tous les comptes (uniquement utilisé pour ADM/COO/CSL)
  const [challengeSection, setChallengeSection] = useState("overview");
  const isKestioInternal = ["ADM", "COO", "CSL"].includes(user.profileType);
  // Décision du 30/09/2026 : le RH et le GES ne se voient plus proposer de s'inscrire à un
  // challenge (règle partagée avec le serveur) ; déjà inscrits, ils gardent leur accès.
  const canJoin = canBeProposedAsParticipant(user.profileType);
  const [briefChallengeId, setBriefChallengeId] = useState<string | null>(null);
  const [joinInterConfirm, setJoinInterConfirm] = useState<any>(null);

  useEffect(() => {
    if (challengeId) return;
    setLoadingList(true);
    challengesApi.listMyChallenges()
      .then(setMyChallenges)
      .catch(() => setMyChallenges([]))
      .finally(() => setLoadingList(false));
  }, [challengeId]);

  // When join popup opens, fetch full challenge data (teams + inter details)
  const [joinChallengeDetails, setJoinChallengeDetails] = useState<any>(null);

  useEffect(() => {
    if (!joinConfirmChallenge) {
      setJoinTeams([]);
      setJoinSelectedTeamId(null);
      setLoadingTeams(false);
      setJoinChallengeDetails(null);
      return;
    }
    setLoadingTeams(true);
    setJoinSelectedTeamId(null);
    challengesApi.getChallenge(joinConfirmChallenge.id)
      .then((ch: any) => {
        // Teams
        if (joinConfirmChallenge.teamAssignmentMode === "participant_choice") {
          const teams = (ch.teams || []).map((team: any) => ({
            id: team.id, name: team.name, color: team.color,
            members: (ch.participants || [])
              .filter((p: any) => p.teamId === team.id)
              .map((p: any) => ({ firstName: p.userFirstName || "", lastName: p.userLastName || "" })),
          }));
          setJoinTeams(teams);
        }
        // Store full details for inter challenges
        setJoinChallengeDetails(ch);
      })
      .catch(() => { setJoinTeams([]); setJoinChallengeDetails(null); })
      .finally(() => setLoadingTeams(false));
  }, [joinConfirmChallenge?.id]);

  // Join handler with confirmation modal
  const handleJoin = async (ch: ActiveChallengeInfo) => {
    const hasTeamChoice = ch.teamAssignmentMode === "participant_choice" && joinTeams.length > 0;
    if (hasTeamChoice && !joinSelectedTeamId) return;
    setJoiningId(ch.id);
    try {
      await challengesApi.joinChallenge(ch.id, joinSelectedTeamId || undefined);
      const updated = await challengesApi.listMyChallenges();
      setMyChallenges(updated);
      setJoinConfirmChallenge(null);
      // Navigate to the challenge page after joining
      if (onSelectChallenge) onSelectChallenge(ch.id);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setJoiningId(null);
    }
  };

  // Leave handler
  const handleLeave = async (chId: string) => {
    if (!confirm(t("challenge.leaveConfirmMessage"))) return;
    setLeavingId(chId);
    try {
      await challengesApi.leaveChallenge(chId);
      const updated = await challengesApi.listMyChallenges();
      setMyChallenges(updated);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLeavingId(null);
    }
  };

  // Helper: objective label — supports static keys + dynamic scoring criteria
  const objectiveLabel = (obj: string | undefined, scoringCriteria?: Array<{ key: string; label: string; labelFr?: string; labelEn?: string }>) => {
    const labels: Record<string, string> = {
      most_rdv: t("challenge.objectiveMostRdv"),
      most_needs: t("challenge.objectiveMostNeeds"),
      deal_closed: t("challenge.objectiveDealClosed"),
      pitch_validated: t("challenge.objectivePitchValidated"),
      tension_mastered: t("challenge.objectiveTensionMastered"),
      clear_outcome: t("challenge.objectiveClearOutcome"),
      best_average: t("challenge.objectiveBestAverage"),
      cumulative_score: t("challenge.objectiveCumulativeScore"),
    };
    if (labels[obj || ""]) return labels[obj || ""];
    // Dynamic: look up in scoring criteria
    if (scoringCriteria && obj) {
      const criterion = scoringCriteria.find(c => c.key === obj);
      if (criterion) {
        return pickScoringLabel(criterion, i18n.language);
      }
    }
    return obj || "";
  };

  // No challengeId → list available challenges
  if (!challengeId) {
    if (loadingList) {
      return (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      );
    }

    return (
      <div>
        <SessionsWorkspace
          title={t("challenge.challenges")}
          subtitle={t("challenge.subtitle")}
          accent="#926B00"
          headerIcon="🏆"
          sections={[
            { id: "overview", label: t("challenge.tabChallenges"), icon: Trophy },
            { id: "duels", label: t("challenge.tabDuels"), icon: Swords },
            { id: "tournaments", label: t("challenge.tabTournaments"), icon: Trophy },
            { id: "enroll", label: t("challenge.enrollSection"), icon: Plus },
            { id: "history", label: t("sessionsTraining.history"), icon: HistoryIcon },
          ]}
          active={challengeSection}
          onChange={setChallengeSection}
        >
          {challengeSection === "overview" && (
            <div className="space-y-5">
              {/* KPIs retirés (doublon de la page Entraînement — hub des engagements). */}
              <div className="flex items-center justify-end flex-wrap gap-4">
          {/* Filtres : Mon entreprise / Plateforme + (ADM/COO/CSL) dropdown par compte client */}
          <div className="flex items-center gap-3 flex-wrap">
            <div data-tour="challenges-filters" className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {([
                { value: "all" as const, label: t("challenge.filterAll") },
                { value: "intra" as const, label: t("challenge.filterIntra") },
                { value: "inter" as const, label: t("challenge.filterInter") },
              ]).map(({ value, label }) => (
                <button key={value} onClick={() => setChallengeFilter(value)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${challengeFilter === value ? "bg-white shadow-sm font-semibold text-[#384872]" : "text-gray-500 hover:text-gray-700"}`}>
                  {label}
                </button>
              ))}
            </div>
            {isKestioInternal && (() => {
              // Liste distincte des comptes clients présents dans myChallenges (pour dropdown ADM).
              const accountsMap = new Map<string, string>();
              for (const ch of myChallenges) {
                const id = (ch as any).clientAccountId;
                const name = (ch as any).clientAccountName;
                if (id && name && !accountsMap.has(id)) accountsMap.set(id, name);
              }
              const accountOptions = Array.from(accountsMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
              return (
                <select
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white text-[#384872] focus:outline-none focus:ring-2 focus:ring-[#384872]/30"
                >
                  <option value="">{t("challenge.filterAllAccounts", "Tous les comptes")}</option>
                  {accountOptions.map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              );
            })()}
          </div>
        </div>
        {(() => {
          const filtered = myChallenges.filter(ch => {
            if (!(ch as any).enrolled) return false; // Vue d'ensemble = mes challenges rejoints
            const chScope = (ch as any).scope || "intra";
            const chAccountId = (ch as any).clientAccountId as string | undefined;
            if (challengeFilter === "intra") {
              // Mon entreprise = scope intra ET du compte cible (dropdown si défini, sinon le mien).
              if (chScope !== "intra") return false;
              const targetAccount = accountFilter || user.clientAccountId;
              if (chAccountId !== targetAccount) return false;
            } else if (challengeFilter === "inter") {
              // Plateforme = challenges inter (public + cross-clients), peu importe le compte créateur.
              if (chScope === "intra") return false;
              if (accountFilter && chAccountId !== accountFilter) return false;
            } else {
              // Tous : dropdown filtre encore par clientAccountId.
              if (accountFilter && chAccountId !== accountFilter) return false;
            }
            return true;
          });
          return filtered;
        })().length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">{t("challenge.noJoined")}</p>
              <p className="text-sm mt-1">{t("challenge.noJoinedDesc")}</p>
            </CardContent>
          </Card>
        ) : (
          /* Fil continu : colonne unique, trié par urgence (fin la plus proche d'abord, terminés en bas). */
          <div className="flex flex-col gap-3">
            {myChallenges.filter(ch => {
              if (!(ch as any).enrolled) return false; // Vue d'ensemble = mes challenges rejoints
              const chScope = (ch as any).scope || "intra";
              const chAccountId = (ch as any).clientAccountId as string | undefined;
              if (challengeFilter === "intra") {
                if (chScope !== "intra") return false;
                const targetAccount = accountFilter || user.clientAccountId;
                if (chAccountId !== targetAccount) return false;
              } else if (challengeFilter === "inter") {
                if (chScope === "intra") return false;
                if (accountFilter && chAccountId !== accountFilter) return false;
              } else {
                if (accountFilter && chAccountId !== accountFilter) return false;
              }
              return true;
            }).sort((a: any, b: any) => {
              const aEnded = a.endDate && new Date(a.endDate) < new Date() ? 1 : 0;
              const bEnded = b.endDate && new Date(b.endDate) < new Date() ? 1 : 0;
              if (aEnded !== bEnded) return aEnded - bEnded;
              const ae = a.endDate ? new Date(a.endDate).getTime() : Infinity;
              const be = b.endDate ? new Date(b.endDate).getTime() : Infinity;
              return ae - be;
            }).map((ch) => {
              const daysLeft = ch.endDate ? Math.max(0, Math.ceil((new Date(ch.endDate).getTime() - Date.now()) / 86400000)) : null;
              return (
                <Card
                  key={ch.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow group relative"
                  style={{ borderLeftWidth: 4, borderLeftColor: ch.simulatorColor || "#384872" }}
                >
                  {/* Bouton désinscription en haut à droite, au-dessus de la barre (challenges optionnels inscrits, non terminés) */}
                  {ch.enrolled && ch.challengeType === "optional" && !(ch.endDate && new Date(ch.endDate) < new Date()) && (
                    <button
                      className="absolute -top-0.5 right-1 z-10 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      disabled={leavingId === ch.id}
                      title={t("challenge.leaveChallenge")}
                      onClick={(e) => { e.stopPropagation(); handleLeave(ch.id); }}
                    >
                      {leavingId === ch.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                    </button>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm" style={{ backgroundColor: ch.simulatorColor || "#384872" }}>
                        {ch.simulatorIcon && ch.simulatorIcon.startsWith("data:") ? (
                          <img src={ch.simulatorIcon} alt="" loading="lazy" className="h-7 w-7 object-contain" />
                        ) : ch.simulatorIcon ? (
                          <span className="text-lg">{ch.simulatorIcon}</span>
                        ) : (
                          <Zap className="h-6 w-6 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="font-bold text-base truncate group-hover:text-blue-700 transition-colors">{pickChallengeTitle(ch, i18n.language)}</h2>
                        <p className="text-sm text-gray-500">{ch.simulatorName}</p>
                        {pickChallengeDescription(ch, i18n.language) && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{pickChallengeDescription(ch, i18n.language)}</p>}
                      </div>
                    </div>
                    {/* Objective + dates + countdown */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {ch.primaryObjective && (
                        <Badge variant="secondary" className="text-xs">{objectiveLabel(ch.primaryObjective)}</Badge>
                      )}
                      {ch.challengeType === "optional" && (
                        <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">{t("challenge.optionalBadge")}</Badge>
                      )}
                      {(ch.startDate || ch.endDate) && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {ch.startDate ? new Date(ch.startDate).toLocaleDateString(t("common.dateLocale"), { day: "numeric", month: "short" }) : "?"}
                          {" → "}
                          {ch.endDate ? new Date(ch.endDate).toLocaleDateString(t("common.dateLocale"), { day: "numeric", month: "short" }) : "?"}
                        </span>
                      )}
                      {(() => {
                        const hasStartedInfo = ch.startDate ? new Date(ch.startDate) <= new Date() : true;
                        const daysUntilStart = ch.startDate ? Math.max(0, Math.ceil((new Date(ch.startDate).getTime() - Date.now()) / 86400000)) : 0;
                        if (!hasStartedInfo && daysUntilStart > 0) {
                          return (
                            <span className="text-xs text-blue-500 flex items-center gap-1 ml-auto">
                              <Calendar className="h-3.5 w-3.5" />
                              {t("challenge.startsInDays", { count: daysUntilStart })}
                            </span>
                          );
                        }
                        if (daysLeft !== null && daysLeft > 0) {
                          return (
                            <span className="text-xs text-orange-500 flex items-center gap-1 ml-auto">
                              <Flame className="h-3.5 w-3.5" />
                              {t("challenge.daysLeftToFinish", { count: daysLeft })}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      {(() => {
                        const isInter = (ch as any).scope && (ch as any).scope !== "intra";
                        const hasStarted = ch.startDate ? new Date(ch.startDate) <= new Date() : true;
                        const hasEnded = ch.endDate ? new Date(ch.endDate) < new Date() : false;
                        // Profils superviseurs : ils CONSULTENT les résultats (le challenge a une
                        // liste restreinte de participants/équipes), ils ne « rejoignent » pas.
                        const canViewResults = ["ORG", "ADM", "COO", "CSL", "MNG"].includes(user.profileType);

                        // En pause (point 1, 30/09/2026) : visible, mais ni partie ni inscription —
                        // on dit pourquoi, et le classement (figé) reste consultable par qui y a accès.
                        if ((ch as { status?: string }).status === "paused") {
                          return (
                            <div className="flex flex-1 flex-wrap items-center gap-2">
                              <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#FBF1D6", color: "#926B00" }}>{t("challenge.statusPaused")}</span>
                              <span className="min-w-0 flex-1 text-xs text-muted-foreground">{t("challenge.pausedNoPlay")}</span>
                              {(ch.enrolled || canViewResults) && (
                                <Button size="sm" variant="outline" className="gap-1" onClick={() => onSelectChallenge?.(ch.id)}>
                                  <Trophy className="h-3.5 w-3.5" />
                                  {t("challenge.leaderboardShort")}
                                </Button>
                              )}
                            </div>
                          );
                        }

                        if (hasEnded) {
                          // Challenge terminé : uniquement "Voir le classement"
                          return (
                            <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => onSelectChallenge?.(ch.id)}>
                              <Trophy className="h-3.5 w-3.5" />
                              {t("challenge.viewLeaderboardShort")}
                            </Button>
                          );
                        }

                        if (ch.enrolled) {
                          // Inscrit
                          return (
                            <>
                              {isInter && (
                                <Button size="sm" variant="outline" className="gap-1" onClick={() => setBriefChallengeId(ch.id)}>
                                  <BookOpen className="h-3.5 w-3.5" />
                                  {t("challenge.briefShort")}
                                </Button>
                              )}
                              {hasStarted && onStartTraining && (
                                <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-500 text-white" onClick={(e) => {
                                  e.stopPropagation();
                                  onStartTraining(
                                    { id: ch.simulatorId || "", name: ch.simulatorName || "", simulatorType: "training", icon: ch.simulatorIcon, color: ch.simulatorColor } as any,
                                    ch.id,
                                  );
                                }}>
                                  <Play className="h-3.5 w-3.5" />
                                  {t("challenge.play")}
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="gap-1" onClick={() => onSelectChallenge?.(ch.id)}>
                                <Trophy className="h-3.5 w-3.5" />
                                {hasStarted
                                  ? t("challenge.leaderboardShort")
                                  : t("challenge.viewChallenge")}
                              </Button>
                              {/* Bouton désinscription déplacé en haut à droite de la card */}
                            </>
                          );
                        }

                        // Non inscrit
                        if (isInter) {
                          // Inter : Brief + S'inscrire (popup confirmation)
                          return (
                            <>
                              <Button size="sm" variant="outline" className="gap-1" onClick={() => setBriefChallengeId(ch.id)}>
                                <BookOpen className="h-3.5 w-3.5" />
                                {t("challenge.briefShort")}
                              </Button>
                              {canJoin && (
                              <Button size="sm" className="flex-1 gap-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold"
                                onClick={() => setJoinInterConfirm(ch)}>
                                {t("challenge.join")}
                              </Button>
                              )}
                            </>
                          );
                        }
                        // Intra non inscrit — superviseur (ORG/MNG/interne) : consultation des
                        // résultats, PAS d'inscription (le challenge a une liste restreinte).
                        if (canViewResults) {
                          return (
                            <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => onSelectChallenge?.(ch.id)}>
                              <Trophy className="h-3.5 w-3.5" />
                              {t("challenge.viewLeaderboardShort")}
                            </Button>
                          );
                        }
                        // Intra non inscrit — bénéficiaire : popup classique d'inscription
                        if (!canJoin) return null;
                        return (
                          <Button size="sm" className="flex-1 gap-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold"
                            disabled={joiningId === ch.id}
                            onClick={(e) => { e.stopPropagation(); setJoinConfirmChallenge(ch); }}>
                            {joiningId === ch.id ? t("challenge.joining") : t("challenge.joinChallengeLong")}
                          </Button>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
            </div>
          )}

          {challengeSection === "duels" && (
            <DuelsView onPlayDuel={onPlayDuel} />
          )}

          {challengeSection === "tournaments" && (
            <TournamentsView profileType={user.profileType} onPlayDuel={onPlayDuel} />
          )}

          {challengeSection === "enroll" && (
            (() => {
              const avail = canJoin ? myChallenges.filter((c: any) => !c.enrolled) : [];
              return avail.length === 0 ? (
                <div className="rounded-2xl border border-dashed py-16 text-center text-sm text-muted-foreground">{t("challenge.noAvailable")}</div>
              ) : (
                <div className="space-y-2">
                  {avail.map((ch: any) => (
                    <div key={ch.id} className="flex items-center gap-3 rounded-xl border bg-white p-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: ch.simulatorColor || "#384872" }}>
                        <Trophy className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium" style={{ color: "#384872" }}>{pickChallengeTitle(ch, i18n.language)}</p>
                        <p className="truncate text-xs text-muted-foreground">{ch.simulatorName}{(ch.scope && ch.scope !== "intra") ? ` · ${t("challenge.filterInter")}` : ""}</p>
                      </div>
                      <Button size="sm" disabled={joiningId === ch.id} onClick={() => ((ch.scope && ch.scope !== "intra") ? setJoinInterConfirm(ch) : setJoinConfirmChallenge(ch))}>
                        <Plus className="mr-1 h-4 w-4" />{t("challenge.joinChallengeLong")}
                      </Button>
                    </div>
                  ))}
                </div>
              );
            })()
          )}

          {challengeSection === "history" && (
            <HistoryPage
              user={user}
              embedded
              onlyTab="challenge"
              onBack={() => setChallengeSection("overview")}
              onOpenChallenge={(id) => onSelectChallenge?.(id)}
              onNavigate={() => { /* noop */ }}
              onOpenTeamHistory={onOpenTeamHistory}
            />
          )}
        </SessionsWorkspace>

        {/* Popup confirmation inscription inter */}
        {joinInterConfirm && (
          <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setJoinInterConfirm(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="p-6 pb-4" style={{ background: `linear-gradient(135deg, ${joinInterConfirm.simulatorColor || "#384872"} 0%, #384872 100%)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Trophy className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{pickChallengeTitle(joinInterConfirm, i18n.language)}</h3>
                    <p className="text-white/70 text-sm">{joinInterConfirm.simulatorName}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600">
                  {t("challenge.interJoinConfirmText")}
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    disabled={!!joiningId}
                    onClick={async () => {
                      try {
                        setJoiningId(joinInterConfirm.id);
                        await challengesApi.joinChallenge(joinInterConfirm.id);
                        const updated = await challengesApi.listMyChallenges();
                        setMyChallenges(updated);
                        setJoinInterConfirm(null);
                        if (onSelectChallenge) onSelectChallenge(joinInterConfirm.id);
                      } catch (err: any) {
                        alert(err.message);
                      } finally {
                        setJoiningId(null);
                      }
                    }}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-300 transition-colors shadow-md"
                  >
                    {joiningId ? t("challenge.joiningShort") : t("challenge.confirmRegistration")}
                  </button>
                  <button
                    onClick={() => { setBriefChallengeId(joinInterConfirm.id); setJoinInterConfirm(null); }}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <BookOpen className="h-4 w-4" />
                    {t("challenge.viewBriefBeforeJoin")}
                  </button>
                  <button
                    onClick={() => setJoinInterConfirm(null)}
                    className="w-full px-4 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Brief overlay */}
        {briefChallengeId && (
          <div className="fixed inset-0 z-[60] bg-white overflow-y-auto">
            <ChallengeBriefPage
              challengeId={briefChallengeId}
              onBack={() => setBriefChallengeId(null)}
              isEnrolled={myChallenges.some(c => c.id === briefChallengeId && c.enrolled)}
              onJoin={!canJoin ? undefined : async () => {
                try {
                  setJoiningId(briefChallengeId);
                  await challengesApi.joinChallenge(briefChallengeId);
                  const updated = await challengesApi.listMyChallenges();
                  setMyChallenges(updated);
                  setBriefChallengeId(null);
                  if (onSelectChallenge) onSelectChallenge(briefChallengeId);
                } catch (err: any) {
                  alert(err.message);
                } finally {
                  setJoiningId(null);
                }
              }}
            />
          </div>
        )}

        {/* Join confirmation modal */}
        {joinConfirmChallenge && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" role="dialog" aria-modal="true" onClick={() => !joiningId && setJoinConfirmChallenge(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="p-6 pb-4" style={{ background: `linear-gradient(135deg, ${joinConfirmChallenge.simulatorColor || "#384872"} 0%, #384872 100%)` }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {joinConfirmChallenge.simulatorIcon?.startsWith("data:") ? (
                      <img src={joinConfirmChallenge.simulatorIcon} alt="" loading="lazy" className="h-7 w-7 object-contain" />
                    ) : joinConfirmChallenge.simulatorIcon ? (
                      <span className="text-xl">{joinConfirmChallenge.simulatorIcon}</span>
                    ) : (
                      <Trophy className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{pickChallengeTitle(joinConfirmChallenge, i18n.language)}</h3>
                    <p className="text-white/70 text-sm">{joinConfirmChallenge.simulatorName}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {pickChallengeDescription(joinConfirmChallenge, i18n.language) && (
                  <div className="animate-[fadeIn_0.3s_ease-out]">
                    <p className="text-gray-600 text-sm leading-relaxed">{pickChallengeDescription(joinConfirmChallenge, i18n.language)}</p>
                  </div>
                )}

                {/* === BRIEF INTER — société fictive, offres, personae === */}
                {joinChallengeDetails?.scope && joinChallengeDetails.scope !== "intra" && (
                  <div className="space-y-3 animate-[fadeIn_0.35s_ease-out]">
                    {/* Société fictive */}
                    {pickChallengeFictiveCompanyName(joinChallengeDetails, i18n.language) && (
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="h-4 w-4 text-[#384872]" />
                          <span className="text-xs font-semibold text-[#384872] uppercase tracking-wide">{t("challenge.theCompany")}</span>
                        </div>
                        <p className="font-semibold text-sm" style={{ color: "#384872" }}>{pickChallengeFictiveCompanyName(joinChallengeDetails, i18n.language)}</p>
                        {pickChallengeFictiveCompanyDescription(joinChallengeDetails, i18n.language) && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{pickChallengeFictiveCompanyDescription(joinChallengeDetails, i18n.language)}</p>
                        )}
                      </div>
                    )}
                    {/* Offres */}
                    {joinChallengeDetails.offers?.length > 0 && (
                      <div className="p-3 rounded-xl bg-green-50/50 border border-green-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="h-4 w-4 text-[#7CBF94]" />
                          <span className="text-xs font-semibold text-[#1E7A3C] uppercase tracking-wide">
                            {t("challenge.offersToPresent", { count: joinChallengeDetails.offers.length })}
                          </span>
                        </div>
                        {joinChallengeDetails.offers.map((o: any, i: number) => (
                          <div key={i} className="mt-1">
                            <span className="text-sm font-medium" style={{ color: "#384872" }}>{o.name}</span>
                            {o.description && <span className="text-xs text-muted-foreground ml-1">— {o.description}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Personae */}
                    {joinChallengeDetails.personae?.length > 0 && (
                      <div className="p-3 rounded-xl bg-purple-50/50 border border-purple-100">
                        <div className="flex items-center gap-2 mb-1">
                          <UserCircle className="h-4 w-4 text-[#988EF2]" />
                          <span className="text-xs font-semibold text-[#5A4BBE] uppercase tracking-wide">
                            {t("challenge.clientProfiles", { count: joinChallengeDetails.personae.length })}
                          </span>
                        </div>
                        {joinChallengeDetails.personae.map((p: any, i: number) => (
                          <div key={i} className="mt-1 text-sm">
                            <span className="font-medium" style={{ color: "#384872" }}>{p.function || p.csp || t("challenge.profileFallback")}</span>
                            <span className="text-xs text-muted-foreground ml-1">({p.personaType} — {p.category === "customer_care" ? t("challenge.personaCategoryCC") : t("challenge.personaCategorySales")})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Récompense */}
                    {(joinChallengeDetails.badge || pickChallengeGiftName(joinChallengeDetails, i18n.language)) && (
                      <div className="p-3 rounded-xl bg-yellow-50/50 border border-yellow-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Star className="h-4 w-4 text-[#F6C847]" />
                          <span className="text-xs font-semibold text-[#926B00] uppercase tracking-wide">{t("challenge.toWin")}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {joinChallengeDetails.badge && (
                            <div className="flex items-center gap-2">
                              <img src={joinChallengeDetails.badge.imageUrl} alt="" className="w-8 h-8 rounded" />
                              <span className="text-sm font-medium">{joinChallengeDetails.badge.name}</span>
                            </div>
                          )}
                          {pickChallengeGiftName(joinChallengeDetails, i18n.language) && (
                            <div className="flex items-center gap-2">
                              {joinChallengeDetails.giftPhotoUrl && <img src={joinChallengeDetails.giftPhotoUrl} alt="" className="w-8 h-8 rounded" />}
                              <span className="text-sm font-medium">{pickChallengeGiftName(joinChallengeDetails, i18n.language)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Lien vers le brief complet */}
                    <button onClick={() => { setJoinConfirmChallenge(null); setShowBrief(true); if (onSelectChallenge) onSelectChallenge(joinConfirmChallenge.id); }}
                      className="w-full text-center text-xs text-[#5A4BBE] hover:underline py-1">
                      {t("challenge.viewFullBrief")}
                    </button>
                  </div>
                )}

                <div className="space-y-3 animate-[fadeIn_0.4s_ease-out]">
                  {/* Objective 1 */}
                  {joinConfirmChallenge.primaryObjective && (
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Target className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{t("challenge.joinObjective1")}</div>
                        <div className="text-sm font-medium text-gray-800">{objectiveLabel(joinConfirmChallenge.primaryObjective, joinConfirmChallenge.simulatorScoringCriteria as any)}</div>
                      </div>
                    </div>
                  )}
                  {/* Objective 2 */}
                  {joinConfirmChallenge.secondaryObjective && (
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
                        <Crosshair className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide">{t("challenge.joinObjective2")}</div>
                        <div className="text-sm font-medium text-gray-800">{objectiveLabel(joinConfirmChallenge.secondaryObjective, joinConfirmChallenge.simulatorScoringCriteria as any)}</div>
                      </div>
                    </div>
                  )}
                  {/* Sessions count */}
                  {joinConfirmChallenge.maxSessions && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                        <Zap className="h-4 w-4 text-gray-600" />
                      </div>
                      <span className="text-sm text-gray-700">{t("challenge.joinSessionsCount", { count: joinConfirmChallenge.maxSessions })}</span>
                    </div>
                  )}
                  {/* Period */}
                  {(joinConfirmChallenge.startDate || joinConfirmChallenge.endDate) && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                        <Calendar className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("challenge.joinPeriod")}</div>
                        <div className="text-sm text-gray-700">
                          {t("challenge.joinPeriodValue", {
                            start: joinConfirmChallenge.startDate ? new Date(joinConfirmChallenge.startDate).toLocaleDateString(t("common.dateLocale"), { day: "numeric", month: "long", year: "numeric" }) : "—",
                            end: joinConfirmChallenge.endDate ? new Date(joinConfirmChallenge.endDate).toLocaleDateString(t("common.dateLocale"), { day: "numeric", month: "long", year: "numeric" }) : "—",
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Time left */}
                  {joinConfirmChallenge.endDate && (() => {
                    const msLeft = new Date(joinConfirmChallenge.endDate).getTime() - Date.now();
                    const days = Math.max(0, Math.ceil(msLeft / 86400000));
                    const hours = Math.max(1, Math.ceil(msLeft / 3600000));
                    if (msLeft <= 0) return null;
                    return (
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 animate-[pulse_3s_ease-in-out_infinite]">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                          <Clock className="h-4 w-4 text-orange-600" />
                        </div>
                        <span className="text-sm font-medium text-orange-700">
                          {days > 0 ? t("challenge.joinTimeLeft", { days }) : t("challenge.joinTimeLeftHours", { hours })}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                {/* Team selection for participant_choice challenges */}
                {joinConfirmChallenge.teamAssignmentMode === "participant_choice" && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">{t("challenge.chooseTeam")}</h4>
                    {loadingTeams ? (
                      <p className="text-sm text-gray-400 italic">{t("challenge.loadingTeams")}</p>
                    ) : (
                      <div className="space-y-2">
                        {joinTeams.map(team => (
                          <button
                            key={team.id}
                            onClick={() => setJoinSelectedTeamId(team.id)}
                            className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                              joinSelectedTeamId === team.id
                                ? "border-yellow-400 bg-yellow-50 shadow-sm"
                                : "border-gray-200 hover:border-gray-300 bg-white"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-4 h-4 rounded-full shrink-0"
                                style={{ backgroundColor: team.color || "#384872" }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900">{team.name}</div>
                                <div className="text-xs text-gray-500">
                                  {t("challenge.teamMembers", { count: team.members.length })}
                                  {team.members.length > 0 && (
                                    <span className="ml-1 text-gray-400">
                                      — {team.members.slice(0, 3).map(m => m.firstName).join(", ")}
                                      {team.members.length > 3 && "..."}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {joinSelectedTeamId === team.id && (
                                <div className="w-5 h-5 rounded-full bg-yellow-400 flex items-center justify-center shrink-0">
                                  <svg className="w-3 h-3 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                        {!loadingTeams && joinTeams.length === 0 && (
                          <p className="text-sm text-gray-400 italic">{t("challenge.noTeams")}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setJoinConfirmChallenge(null)}
                    disabled={!!joiningId}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={() => handleJoin(joinConfirmChallenge)}
                    disabled={!!joiningId || (joinConfirmChallenge.teamAssignmentMode === "participant_choice" && joinTeams.length > 0 && !joinSelectedTeamId)}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors shadow-md ${
                      joinConfirmChallenge.teamAssignmentMode === "participant_choice" && joinTeams.length > 0 && !joinSelectedTeamId
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-yellow-400 text-gray-900 hover:bg-yellow-300"
                    }`}
                  >
                    {joiningId ? t("challenge.joining") : t("challenge.joinConfirmBtn")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-500">{t("challenge.loadingLeaderboard")}</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("challenge.backToList")}
            </Button>
          )}
        </div>
        <Card>
          <CardContent className="py-16 text-center text-red-500">
            <p>{error || t("challenge.errorLoading")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Affichage du brief
  if (showBrief && challengeId) {
    return (
      <ChallengeBriefPage
        challengeId={challengeId}
        onBack={() => setShowBrief(false)}
        isEnrolled={true}
      />
    );
  }

  const { challenge, teams, leaderboard, teamLeaderboard } = data;

  // Filter by team
  const filteredLeaderboard = selectedTeamId === "all"
    ? leaderboard
    : leaderboard.filter(e => e.teamId === selectedTeamId);

  const top3 = filteredLeaderboard.slice(0, 3);

  // Current user's entry
  const currentUserEntry = leaderboard.find(e => e.userId === user.id);
  const isParticipant = !!currentUserEntry;
  const isActive = challenge.status === "active";

  // My stats
  const mySessions = currentUserEntry?.sessionsCompleted || 0;
  const myMeetings = currentUserEntry?.meetingsBooked || 0;
  const myTotalScore = currentUserEntry?.totalScore || 0;
  const myAverageScore = currentUserEntry?.averageScore || 0;
  const myRankingScore = currentUserEntry?.rankingScore || 0;
  const mySecondaryScore = currentUserEntry?.secondaryScore || 0;

  // Check if user can start a session: must be participant, within period, and under max sessions
  const now = new Date();
  const challengeStarted = !challenge.startDate || new Date(challenge.startDate) <= now;
  const challengeNotExpired = !challenge.endDate || new Date(challenge.endDate) > now;
  const withinPeriod = challengeStarted && challengeNotExpired;
  const underMaxSessions = !!currentUserEntry && currentUserEntry.sessionsCompleted < challenge.maxSessions;
  const canStartSession = isActive && isParticipant && withinPeriod && underMaxSessions;

  const statusColors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700",
    active: "bg-green-100 text-green-700",
    paused: "bg-yellow-100 text-yellow-800",
    ended: "bg-blue-100 text-blue-700",
  };
  const statusLabels: Record<string, string> = {
    draft: t("challenge.statusDraft"),
    active: t("challenge.statusActive"),
    paused: t("challenge.statusPaused"),
    ended: t("challenge.statusEnded"),
  };

  // Boolean objectives use count, score objectives use pts
  const BOOLEAN_OBJECTIVES = ["most_rdv", "most_needs", "deal_closed", "pitch_validated", "tension_mastered", "clear_outcome"];
  const isBooleanObjective = (obj: string | undefined) => BOOLEAN_OBJECTIVES.includes(obj || "");

  // Get the user's own value for a given objective
  const getMyObjectiveValue = (obj: string | undefined) => {
    if (!obj || !currentUserEntry) return 0;
    if (isBooleanObjective(obj)) return currentUserEntry.rankingScore || 0;
    if (obj === "best_average") return myAverageScore;
    if (obj === "cumulative_score") return myTotalScore;
    // Dynamic scoring criteria objective → use rankingScore computed by backend
    return currentUserEntry.rankingScore || 0;
  };

  // Format an objective value for display
  const formatObjectiveValue = (obj: string | undefined, value: number) => {
    if (isBooleanObjective(obj)) return `${value}`;
    return `${value} ${t("challenge.pts")}`;
  };

  // Primary objective display for stats card
  const renderPrimaryObjectiveCard = () => {
    const obj = challenge.primaryObjective;
    const label = objectiveLabel(obj, challenge.scoringCriteria);
    const value = getMyObjectiveValue(obj);
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{label}</CardTitle>
          {isBooleanObjective(obj) ? <Target className="h-4 w-4 text-muted-foreground" /> : <TrendingUp className="h-4 w-4 text-muted-foreground" />}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatObjectiveValue(obj, value)}</div>
          <p className="text-xs text-muted-foreground">{label}</p>
        </CardContent>
      </Card>
    );
  };

  // Secondary objective display for stats card
  const renderSecondaryObjectiveCard = () => {
    if (!challenge.secondaryObjective) return null;
    const obj = challenge.secondaryObjective;
    const label = objectiveLabel(obj, challenge.scoringCriteria);
    const myEntry = currentUserEntry;
    let value: number;
    if (isBooleanObjective(obj)) {
      value = myEntry?.secondaryScore || 0;
    } else if (obj === "best_average") {
      value = myAverageScore;
    } else if (obj === "cumulative_score") {
      value = myTotalScore;
    } else {
      value = myEntry?.secondaryScore || 0;
    }
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{t("challenge.secondaryObjective")}</CardTitle>
          <Trophy className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatObjectiveValue(obj, value)}</div>
          <p className="text-xs text-muted-foreground">{label}</p>
        </CardContent>
      </Card>
    );
  };

  // Score display label based on primary objective for podium
  const getScoreLabel = (entry: LeaderboardEntry) => {
    return formatObjectiveValue(challenge.primaryObjective, entry.rankingScore);
  };

  // Secondary objective score for leaderboard table
  const getSecondaryValue = (entry: LeaderboardEntry) => {
    if (!challenge.secondaryObjective) return null;
    return formatObjectiveValue(challenge.secondaryObjective, entry.secondaryScore);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <Trophy className="h-7 w-7 text-yellow-500" />
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Sora, sans-serif', color: '#384872' }}>
                {pickChallengeTitle(challenge, i18n.language)}
              </h1>
              <Badge className={`text-xs ${statusColors[challenge.status] || ""}`}>
                {statusLabels[challenge.status] || challenge.status}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              {challenge.simulatorIcon && challenge.simulatorIcon.startsWith("data:") ? (
                <img src={challenge.simulatorIcon} alt="" loading="lazy" className="h-4 w-4 object-contain" />
              ) : challenge.simulatorIcon ? (
                <span>{challenge.simulatorIcon}</span>
              ) : null}
              <span>{challenge.simulatorName}</span>
              {challenge.startDate && (
                <span>
                  {new Date(challenge.startDate).toLocaleDateString()} → {challenge.endDate ? new Date(challenge.endDate).toLocaleDateString() : "?"}
                </span>
              )}
            </div>
            {pickChallengeDescription(challenge, i18n.language) && (
              <p className="text-sm text-gray-500 mt-1">{pickChallengeDescription(challenge, i18n.language)}</p>
            )}
            {/* Bouton Revoir le brief (challenges inter uniquement) */}
            {challenge.scope && challenge.scope !== "intra" && (
              <Button variant="outline" size="sm" onClick={() => setShowBrief(true)} className="mt-2">
                <BookOpen className="h-4 w-4 mr-1" />
                {t("challenge.reviewBrief")}
              </Button>
            )}
            {/* Étape 9B — override du mode de récompense pour CE challenge. */}
            {canManageRewards && (
              <div className="mt-3 max-w-xl">
                <ScopeRewardsModeControl
                  scope="challenge"
                  id={challenge.id}
                  initialMode={(challenge as { rewardsMode?: "virtual" | "catalog" | null }).rewardsMode ?? null}
                  tenantMode={tenantMode}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Team filter */}
          {teams.length >= 2 && (
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t("challenge.selectGroup")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("challenge.allTeams")}</SelectItem>
                {teams.map(team => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Start session button */}
          {canStartSession && onStartTraining && (
            <Button
              onClick={() => onStartTraining(
                { id: challenge.simulatorId, name: challenge.simulatorName, simulatorType: "training", icon: challenge.simulatorIcon, color: challenge.simulatorColor } as AIAgent,
                challenge.id,
              )}
              style={{ backgroundColor: '#384872' }}
            >
              <Play className="h-4 w-4 mr-2" />
              {t("challenge.launchSession")}
            </Button>
          )}
          {challenge.status === "paused" && (
            <Badge variant="secondary">{t("challenge.pausedNoPlay")}</Badge>
          )}
          {isParticipant && !canStartSession && challenge.status !== "paused" && (
            <>
              {!challengeStarted && (
                <Badge variant="secondary">{t("challenge.challengeNotStarted", { date: new Date(challenge.startDate!).toLocaleDateString() })}</Badge>
              )}
              {challengeStarted && !challengeNotExpired && (
                <Badge variant="secondary">{t("challenge.challengeEnded")}</Badge>
              )}
              {withinPeriod && !underMaxSessions && (
                <Badge variant="secondary">{t("challenge.completedAllSessions", { count: mySessions })}</Badge>
              )}
            </>
          )}
          {!isParticipant && challenge.status === "ended" && (
            <Badge variant="secondary">{t("challenge.challengeEnded")}</Badge>
          )}
        </div>
      </div>

      {/* Podium + Stats */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Podium */}
        <Card className="w-full lg:w-2/3">
          <CardHeader>
            <CardTitle className="text-center" style={{ fontFamily: 'Sora, sans-serif', color: '#384872' }}>
              {t("challenge.podium")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <style>{`
              @keyframes riseUp2-mobile { 0% { height: 0; opacity: 0; } 100% { height: 90px; opacity: 1; } }
              @keyframes riseUp1-mobile { 0% { height: 0; opacity: 0; } 100% { height: 120px; opacity: 1; } }
              @keyframes riseUp3-mobile { 0% { height: 0; opacity: 0; } 100% { height: 70px; opacity: 1; } }
              @keyframes riseUp2-desktop { 0% { height: 0; opacity: 0; } 100% { height: 130px; opacity: 1; } }
              @keyframes riseUp1-desktop { 0% { height: 0; opacity: 0; } 100% { height: 170px; opacity: 1; } }
              @keyframes riseUp3-desktop { 0% { height: 0; opacity: 0; } 100% { height: 100px; opacity: 1; } }
              @keyframes anim1 {
                0% { transform: translateY(0); } 2% { transform: translateY(-10px); } 4% { transform: translateY(-6px); }
                6% { transform: translateY(-12px); } 8% { transform: translateY(0); } 8.01%, 100% { transform: translateY(0); }
              }
              @keyframes dance1 {
                0% { transform: rotate(0deg); } 2% { transform: rotate(-15deg); } 4% { transform: rotate(12deg); }
                6% { transform: rotate(-10deg); } 8% { transform: rotate(0deg); } 8.01%, 100% { transform: rotate(0deg); }
              }
              @keyframes anim2 {
                0%, 23% { transform: translateY(0); } 25% { transform: translateY(-10px); } 27% { transform: translateY(-6px); }
                29% { transform: translateY(-12px); } 31% { transform: translateY(0); } 31.01%, 100% { transform: translateY(0); }
              }
              @keyframes dance2 {
                0%, 23% { transform: rotate(0deg); } 25% { transform: rotate(-15deg); } 27% { transform: rotate(12deg); }
                29% { transform: rotate(-10deg); } 31% { transform: rotate(0deg); } 31.01%, 100% { transform: rotate(0deg); }
              }
              @keyframes anim3 {
                0%, 46% { transform: translateY(0); } 48% { transform: translateY(-8px); } 50% { transform: translateY(-5px); }
                52% { transform: translateY(-10px); } 54% { transform: translateY(0); } 54.01%, 100% { transform: translateY(0); }
              }
              @keyframes dance3 {
                0%, 46% { transform: rotate(0deg); } 48% { transform: rotate(-15deg); } 50% { transform: rotate(12deg); }
                52% { transform: rotate(-10deg); } 54% { transform: rotate(0deg); } 54.01%, 100% { transform: rotate(0deg); }
              }
              @keyframes popIn {
                0% { opacity: 0; transform: scale(0) rotate(-20deg); } 70% { transform: scale(1.2) rotate(5deg); }
                85% { transform: scale(0.9) rotate(-3deg); } 100% { opacity: 1; transform: scale(1) rotate(0deg); }
              }
              @keyframes bounceIn {
                0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.1); }
                70% { transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); }
              }
              .podium-1 { animation: riseUp1-mobile 0.8s ease-out 0.3s forwards, anim1 20s ease-in-out 2s infinite; }
              .podium-2 { animation: riseUp2-mobile 0.8s ease-out forwards, anim2 20s ease-in-out 2s infinite; }
              .podium-3 { animation: riseUp3-mobile 0.8s ease-out 0.6s forwards, anim3 20s ease-in-out 2s infinite; }
              @media (min-width: 768px) {
                .podium-1 { animation: riseUp1-desktop 0.8s ease-out 0.3s forwards, anim1 20s ease-in-out 2s infinite; }
                .podium-2 { animation: riseUp2-desktop 0.8s ease-out forwards, anim2 20s ease-in-out 2s infinite; }
                .podium-3 { animation: riseUp3-desktop 0.8s ease-out 0.6s forwards, anim3 20s ease-in-out 2s infinite; }
              }
              .number-1 { animation: popIn 0.7s ease-out forwards, dance1 20s ease-in-out 2s infinite; transform-origin: center bottom; }
              .number-2 { animation: popIn 0.7s ease-out forwards, dance2 20s ease-in-out 2s infinite; transform-origin: center bottom; }
              .number-3 { animation: popIn 0.7s ease-out forwards, dance3 20s ease-in-out 2s infinite; transform-origin: center bottom; }
              .avatar-animate { animation: bounceIn 0.6s ease-out forwards; }
            `}</style>

            {top3.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Users className="h-10 w-10 mx-auto mb-3" />
                <p>{t("challenge.noParticipants")}</p>
              </div>
            ) : (
              <div className="flex items-end justify-center gap-2 md:gap-4 pt-12 md:pt-16 pb-4">
                {/* 2nd place */}
                {top3[1] && (
                  <div className="flex flex-col items-center">
                    <span className="text-2xl md:text-4xl font-bold mb-2 md:mb-3 number-2" style={{ color: '#F49390', fontFamily: 'Sora, sans-serif', animationDelay: '0.8s', opacity: 0 }}>2</span>
                    <div className="w-20 md:w-28 rounded-t-xl flex flex-col items-center justify-center relative podium-2" style={{ backgroundColor: '#384872', height: 0 }}>
                      <div className="avatar-animate" style={{ animationDelay: '1.2s', opacity: 0, marginTop: '-20px' }}>
                        <Avatar className="h-12 w-12 md:h-16 md:w-16 border-2 md:border-4 border-white shadow-lg">
                          <AvatarImage src={top3[1].avatar} />
                          <AvatarFallback className="text-sm md:text-lg bg-gray-100" style={{ color: '#384872' }}>
                            {top3[1].firstName[0]}{top3[1].lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                    <p className="font-semibold text-xs md:text-sm text-center mt-2 md:mt-3 max-w-[80px] md:max-w-none truncate" style={{ color: '#384872' }}>{top3[1].firstName} {top3[1].lastName}</p>
                    <p className="font-bold text-sm md:text-lg" style={{ color: '#F49390' }}>{getScoreLabel(top3[1])}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground">{t("challenge.averageCol")} {top3[1].averageScore}</p>
                  </div>
                )}

                {/* 1st place */}
                {top3[0] && (
                  <div className="flex flex-col items-center">
                    <span className="text-3xl md:text-5xl font-bold mb-2 md:mb-3 number-1" style={{ color: '#F6C847', fontFamily: 'Sora, sans-serif', animationDelay: '1.1s', opacity: 0 }}>1</span>
                    <div className="w-24 md:w-32 rounded-t-xl flex flex-col items-center justify-start pt-3 md:pt-4 relative podium-1" style={{ backgroundColor: '#F6C847', height: 0 }}>
                      <div className="avatar-animate" style={{ animationDelay: '1.5s', opacity: 0 }}>
                        <Avatar className="h-14 w-14 md:h-20 md:w-20 border-2 md:border-4 border-white shadow-lg">
                          <AvatarImage src={top3[0].avatar} />
                          <AvatarFallback className="text-base md:text-xl bg-gray-100" style={{ color: '#384872' }}>
                            {top3[0].firstName[0]}{top3[0].lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                    <p className="font-semibold text-xs md:text-sm text-center mt-2 md:mt-3 max-w-[80px] md:max-w-none truncate" style={{ color: '#384872' }}>{top3[0].firstName} {top3[0].lastName}</p>
                    <p className="font-bold text-base md:text-xl" style={{ color: '#F6C847' }}>{getScoreLabel(top3[0])}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground">{t("challenge.averageCol")} {top3[0].averageScore}</p>
                  </div>
                )}

                {/* 3rd place */}
                {top3[2] && (
                  <div className="flex flex-col items-center">
                    <span className="text-2xl md:text-4xl font-bold mb-2 md:mb-3 number-3" style={{ color: '#988EF2', fontFamily: 'Sora, sans-serif', animationDelay: '1.4s', opacity: 0 }}>3</span>
                    <div className="w-20 md:w-28 rounded-t-xl flex flex-col items-center justify-end pb-2 relative podium-3" style={{ backgroundColor: '#988EF2', height: 0 }}>
                      <div className="avatar-animate" style={{ animationDelay: '1.8s', opacity: 0 }}>
                        <Avatar className="h-12 w-12 md:h-16 md:w-16 border-2 md:border-4 border-white shadow-lg">
                          <AvatarImage src={top3[2].avatar} />
                          <AvatarFallback className="text-sm md:text-lg bg-gray-100" style={{ color: '#384872' }}>
                            {top3[2].firstName[0]}{top3[2].lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                    <p className="font-semibold text-xs md:text-sm text-center mt-2 md:mt-3 max-w-[80px] md:max-w-none truncate" style={{ color: '#384872' }}>{top3[2].firstName} {top3[2].lastName}</p>
                    <p className="font-bold text-sm md:text-lg" style={{ color: '#988EF2' }}>{getScoreLabel(top3[2])}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground">{t("challenge.averageCol")} {top3[2].averageScore}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Cards — MY stats */}
        <div className="w-full lg:w-1/3 flex flex-col gap-2 lg:gap-4">
          {/* Mobile — compact */}
          <div className="grid grid-cols-2 gap-2 lg:hidden">
            <Card className="p-2">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">{t("challenge.participants")}</span>
                </div>
                <div className="text-lg font-bold">{filteredLeaderboard.length}</div>
              </div>
            </Card>
            <Card className="p-2">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <Target className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">{t("challenge.mySessions")}</span>
                </div>
                <div className="text-lg font-bold">{mySessions} / {challenge.maxSessions}</div>
              </div>
            </Card>
            <Card className="p-2">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  {isBooleanObjective(challenge.primaryObjective) ? <Target className="h-3 w-3 text-muted-foreground" /> : <TrendingUp className="h-3 w-3 text-muted-foreground" />}
                  <span className="text-xs font-medium text-muted-foreground">
                    {objectiveLabel(challenge.primaryObjective, challenge.scoringCriteria)}
                  </span>
                </div>
                <div className="text-lg font-bold">
                  {formatObjectiveValue(challenge.primaryObjective, getMyObjectiveValue(challenge.primaryObjective))}
                </div>
              </div>
            </Card>
            {challenge.secondaryObjective && (
              <Card className="p-2">
                <div className="flex flex-col">
                  <div className="flex items-center gap-1 mb-1">
                    <Trophy className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">{objectiveLabel(challenge.secondaryObjective, challenge.scoringCriteria)}</span>
                  </div>
                  <div className="text-lg font-bold">
                    {formatObjectiveValue(challenge.secondaryObjective, currentUserEntry?.secondaryScore || 0)}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Desktop */}
          <div className="hidden lg:flex lg:flex-col lg:gap-4 lg:h-full lg:justify-between">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t("challenge.participants")}</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredLeaderboard.length}</div>
                {selectedTeamId !== "all" && (
                  <p className="text-xs text-muted-foreground">{teams.find(t => t.id === selectedTeamId)?.name}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t("challenge.mySessions")}</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{t("challenge.mySessionsOf", { done: mySessions, max: challenge.maxSessions })}</div>
                <p className="text-xs text-muted-foreground">{t("challenge.sessionsCompleted")}</p>
              </CardContent>
            </Card>

            {renderPrimaryObjectiveCard()}
            {renderSecondaryObjectiveCard()}
          </div>
        </div>
      </div>

      {/* Team ranking (challenges à ≥ 2 équipes) */}
      {teamLeaderboard && teamLeaderboard.length >= 2 && (
        <Card>
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="text-base md:text-lg">{t("challenge.teamRanking")}</CardTitle>
          </CardHeader>
          <CardContent className="px-3 md:px-6">
            <div className="space-y-2">
              {teamLeaderboard.map((team, index) => {
                const medal = index === 0 ? "\u{1F947}" : index === 1 ? "\u{1F948}" : index === 2 ? "\u{1F949}" : "";
                return (
                  <div key={team.teamId} className="flex items-center gap-3 p-2 md:p-3 rounded-lg bg-muted/50">
                    <div className="w-8 text-center font-bold text-sm md:text-base">{medal || `#${index + 1}`}</div>
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: team.color || "#94a3b8" }} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm md:text-base truncate">{team.name}</p>
                      <p className="text-[10px] md:text-xs text-muted-foreground">{t("challenge.teamMembers", { count: team.memberCount })}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-sm md:text-base">{formatObjectiveValue(challenge.primaryObjective, team.primaryTotal)}</p>
                      <p className="text-[10px] text-muted-foreground">{objectiveLabel(challenge.primaryObjective, challenge.scoringCriteria)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Table */}
      <Card>
        <CardHeader className="pb-2 md:pb-4">
          <CardTitle className="text-base md:text-lg">{t("challenge.fullRanking")}</CardTitle>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          {filteredLeaderboard.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>{t("challenge.noParticipants")}</p>
            </div>
          ) : (
            <>
              {/* Table header — desktop */}
              <div className={`hidden md:grid ${challenge.secondaryObjective ? "md:grid-cols-[3rem_1fr_5rem_5rem_5rem_5rem]" : "md:grid-cols-[3rem_1fr_5rem_5rem_5rem]"} gap-2 px-3 pb-2 text-xs font-medium text-muted-foreground uppercase`}>
                <div>#</div>
                <div>{t("challenge.participants")}</div>
                <div className="text-center">{t("challenge.sessionsCol")}</div>
                <div className="text-center">{objectiveLabel(challenge.primaryObjective, challenge.scoringCriteria)}</div>
                <div className="text-center">{t("challenge.averageCol")}</div>
                {challenge.secondaryObjective && <div className="text-center">{objectiveLabel(challenge.secondaryObjective, challenge.scoringCriteria)}</div>}
              </div>

              <div className="space-y-2 md:space-y-1">
                {filteredLeaderboard.map((entry, index) => {
                  const medal = index === 0 ? "\u{1F947}" : index === 1 ? "\u{1F948}" : index === 2 ? "\u{1F949}" : "";
                  const isMe = entry.userId === user.id;

                  return (
                    <div
                      key={entry.userId}
                      className={`flex items-center gap-2 md:grid ${challenge.secondaryObjective ? "md:grid-cols-[3rem_1fr_5rem_5rem_5rem_5rem]" : "md:grid-cols-[3rem_1fr_5rem_5rem_5rem]"} md:gap-2 p-2 md:p-3 rounded-lg ${
                        isMe ? "bg-primary/10 border border-primary" : "bg-muted/50"
                      }`}
                    >
                      {/* Rank */}
                      <div className="w-6 md:w-auto text-center font-bold text-sm md:text-base">
                        {medal || `#${index + 1}`}
                      </div>

                      {/* Avatar + Name */}
                      <div className="flex items-center gap-2 min-w-0 flex-1 md:flex-none">
                        <Avatar className="h-8 w-8 md:h-10 md:w-10 shrink-0">
                          <AvatarImage src={entry.avatar} />
                          <AvatarFallback className="text-xs md:text-sm">
                            {entry.firstName[0]}{entry.lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-medium text-sm md:text-base truncate">
                            {entry.firstName} {entry.lastName}
                          </p>
                          {entry.teamName && (
                            <Badge variant="outline" className="text-[10px] py-0" style={entry.teamColor ? { borderColor: entry.teamColor, color: entry.teamColor } : undefined}>
                              {entry.teamName}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Stats columns — mobile: inline, desktop: grid cells */}
                      <div className="hidden md:flex items-center justify-center text-sm font-medium">
                        {entry.sessionsCompleted}
                      </div>
                      <div className="hidden md:flex items-center justify-center text-sm font-medium">
                        {formatObjectiveValue(challenge.primaryObjective, entry.rankingScore)}
                      </div>
                      <div className="hidden md:flex items-center justify-center text-sm font-bold">
                        {entry.averageScore}
                      </div>
                      {challenge.secondaryObjective && (
                        <div className="hidden md:flex items-center justify-center text-sm">
                          {getSecondaryValue(entry)}
                        </div>
                      )}

                      {/* Mobile compact stats */}
                      <div className="md:hidden text-right shrink-0">
                        <p className="font-bold text-sm">{getScoreLabel(entry)}</p>
                        <p className="text-[10px] text-muted-foreground">{t("challenge.averageCol")} {entry.averageScore} · {entry.sessionsCompleted} {t("challenge.sessionsCol").toLowerCase()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
